import React, { useState, useEffect } from 'react';
import api from '../../api';

const CompetencyHistory = ({ competencyId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [competencyId, page, size]);

  const fetchHistory = async () => {
    if (!competencyId) {
      console.warn('🚫 CompetencyHistory: No competencyId provided');
      return;
    }
    
    console.log(`🔍 CompetencyHistory: Fetching history for competencyId: ${competencyId}`);
    setLoading(true);
    
    try {
      const url = `/audit-history/competency/${competencyId}?page=${page}&size=${size}`;
      console.log(`📡 API Request: ${url}`);
      
      const response = await api.get(url);
      console.log(`✅ API Response:`, response.data);
      console.log(`📊 Audit History Count:`, response.data.content?.length || 0);
      
      setHistory(response.data.content || []);
    } catch (error) {
      console.error('❌ Error fetching competency history:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (actionType) => {
    const map = {
      CREATE: 'bg-success',
      UPDATE: 'bg-info',
      DELETE: 'bg-danger',
      APPROVE: 'bg-success',
      REJECT: 'bg-danger',
      REQUEST_REVISION: 'bg-warning',
      ACTIVATE: 'bg-success',
      DEACTIVATE: 'bg-secondary'
    };
    return <span className={`badge text-white ${map[actionType] || 'bg-light'}`}>{actionType}</span>;
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-warning',
      approved: 'bg-success',
      rejected: 'bg-danger',
      revision_requested: 'bg-info',
      need_revision: 'bg-info',
      ACTIVE: 'bg-success',
      INACTIVE: 'bg-secondary'
    };
    return <span className={`badge text-white ${map[status] || 'bg-light'}`}>{status}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB');
  };

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry);
    setShowDetails(true);
  };

  return (
    <div className="competency-history">
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : history.length === 0 ? (
        <div className="alert alert-info mb-0">No history available</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Date/Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Status Change</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id}>
                  <td className="small text-muted">{formatDate(entry.actionTimestamp)}</td>
                  <td className="small">{entry.userName}</td>
                  <td>{getActionBadge(entry.actionType)}</td>
                  <td className="small">
                    {entry.oldStatus && entry.newStatus ? (
                      <>
                        {getStatusBadge(entry.oldStatus)} → {getStatusBadge(entry.newStatus)}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleViewDetails(entry)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Modal */}
      <div
        className={`modal fade ${showDetails ? 'show' : ''}`}
        style={{ display: showDetails ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
        aria-hidden={!showDetails}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header border-bottom">
              <h5 className="modal-title">Competency History Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowDetails(false)}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {selectedEntry && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Status Information</h6>
                      <div className="mb-2">
                        <strong>Previous Status:</strong><br/>
                        {selectedEntry.oldStatus ? getStatusBadge(selectedEntry.oldStatus) : 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>New Status:</strong><br/>
                        {selectedEntry.newStatus ? getStatusBadge(selectedEntry.newStatus) : 'N/A'}
                      </div>
                      <div className="mb-2">
                        <strong>Request Type:</strong><br/>
                        {selectedEntry.oldRequestType && selectedEntry.newRequestType !== selectedEntry.oldRequestType ? (
                          <>Previous: {selectedEntry.oldRequestType}<br/>New: {selectedEntry.newRequestType}</>
                        ) : (
                          selectedEntry.newRequestType || selectedEntry.oldRequestType || 'N/A'
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6>Review Information</h6>
                      {selectedEntry.reviewDecision && (
                        <div className="mb-2">
                          <strong>Review Decision:</strong><br/>
                          <span className={`badge text-white ${selectedEntry.reviewDecision === 'APPROVE' ? 'bg-success' : 'bg-danger'}`}>
                            {selectedEntry.reviewDecision}
                          </span>
                        </div>
                      )}
                      {selectedEntry.reviewNotes && (
                        <div className="mb-2">
                          <strong>Review Notes:</strong><br/>
                          <p className="text-muted">{selectedEntry.reviewNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <hr />
                  <div>
                    <strong>User:</strong> {selectedEntry.userName}<br/>
                    <strong>Date:</strong> {formatDate(selectedEntry.actionTimestamp)}<br/>
                    <strong>IP Address:</strong> <code>{selectedEntry.ipAddress || 'N/A'}</code><br/>
                    {selectedEntry.description && (
                      <>
                        <strong>Description:</strong> <p>{selectedEntry.description}</p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer border-top">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDetails && <div className="modal-backdrop fade show" onClick={() => setShowDetails(false)}></div>}
    </div>
  );
};

export default CompetencyHistory;
